import React from 'react';
import is from 'is_js';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';

import * as siteSearchActions from '@redux/modules/siteSearch';

import {
  getUrlParameterByName,
} from '@services/Url';
import {
  ErrorMessage,
  ResultsContainer,
  ResultsHeader,
  ResultsTitle,
  ResultsWrapper,
  FlexWrapper,
} from '@components/SearchResults';
import FilterCloud from '@structures/FilterCloud';

import searchFilterApiAdapter from './utils/searchFilterApiAdapter';

import CleSection from './components/CleSection';
import FindALawyerSection from './components/FindALawyerSection';
import MeetingsEventsSection from './components/MeetingsEventsSection';
import ResourcesArticlesSection from './components/ResourcesArticlesSection';
import SitePagesSection from './components/SitePagesSection';
import GreenbookResultsSection from './components/GreenbookResultsSection';

class Results extends React.Component {
  constructor(props) {
    super(props);

    const { dispatch } = this.props;

    this.siteSearchActions = bindActionCreators(siteSearchActions, dispatch);

    this.renderResults = this.renderResults.bind(this);
    this.search = this.search.bind(this);
  }

  componentDidMount() {
    const urlSearchFilters = JSON.parse(getUrlParameterByName('search'));

    if (!urlSearchFilters) {
      this.search();
    }

    if (urlSearchFilters) {
      this.siteSearchActions.filtersRestore(urlSearchFilters);
    }
  }

  componentDidUpdate(prevProps) {
    const prevSearchCriteria = prevProps.siteSearch.filters;
    const searchCriteria = this.props.siteSearch.filters;

    window.history.replaceState(
      null,
      null,
      `?search=${encodeURIComponent(JSON.stringify(searchCriteria))}`,
    );

    if (prevSearchCriteria !== searchCriteria) {
      this.search();
    }
  }

  search() {
    const searchFilterValues = this.props.siteSearch.filters;
    const searchFilterApiData = searchFilterApiAdapter(searchFilterValues);

    this.siteSearchActions.search(searchFilterApiData);
  }

  renderResults() {
    const {
      Cles,
      Events,
      Lawyers,
      Pages,
      Resources,
      EBarGreenbookResults,
      SearchLinks,
    } = this.props.siteSearch.results;
    const {
      keyword,
      section,
    } = this.props.siteSearch.filters;

    return (
      <React.Fragment>
        {(is.empty(section) || is.not.empty(section.filter(x => x.filterId === 'Site Pages'))) && (
          <SitePagesSection
            items={Pages}
            keyword={keyword}
          />
        )}
        {(is.empty(section) || is.not.empty(section.filter(x => x.filterId === 'CLEs'))) && (
          <CleSection
            items={Cles}
            searchLink={SearchLinks.ClesSearchUrl}
          />
        )}
        {(is.empty(section) || is.not.empty(section.filter(x => x.filterId === 'Find a Lawyer'))) && (
          <FindALawyerSection
            items={Lawyers}
            searchLink={SearchLinks.LawyerSearchUrl}
          />
        )}
        {(is.empty(section) || is.not.empty(section.filter(x => x.filterId === 'Resources/Articles'))) && (
          <ResourcesArticlesSection
            items={Resources}
            searchLink={SearchLinks.PracticeLibrarySearchUrl}
          />
        )}
        {(is.empty(section) || is.not.empty(section.filter(x => x.filterId === 'Meetings & Events'))) && (
          <MeetingsEventsSection
            items={Events}
            searchLink={SearchLinks.EventSearchUrl}
          />
        )}
        {this.props.isMember && (is.empty(section) || is.not.empty(section.filter(x => x.filterId === 'Greenbook Results'))) && (
          <GreenbookResultsSection
            items={EBarGreenbookResults}
            searchLink={SearchLinks.EBarGreenbookSearchUrl}
          />
        )}
      </React.Fragment>
    );
  }

  render() {
    const { siteSearch } = this.props;

    return (
      <FlexWrapper>
        <FilterCloud
          searchFilterValues={siteSearch.filters}
          updateSearchFilter={this.siteSearchActions.filtersUpdate}
          clearSearchFilters={this.siteSearchActions.filtersClear}
        />
        <ResultsWrapper>
          <ResultsHeader>
            <ResultsTitle>
              Results
              <span
                id="resultsCountHeader"
              >
                {siteSearch && ` (${siteSearch.count})`}
              </span>
            </ResultsTitle>
          </ResultsHeader>
          <ResultsContainer
            id="resultsContainer"
            style={{
              paddingTop: 0,
            }}
          >
            {siteSearch.error && (
              <ErrorMessage
                message={`Search error - ${siteSearch.error}`}
              />
            )}
            {!siteSearch.loading && !siteSearch.error && this.renderResults()}
          </ResultsContainer>
        </ResultsWrapper>
      </FlexWrapper>
    );
  }
}

Results.propTypes = {
  siteSearch: PropTypes.shape({
    results: PropTypes.shape({
      Cles: PropTypes.arrayOf(PropTypes.object),
      Events: PropTypes.arrayOf(PropTypes.object),
      Count: PropTypes.number,
      Lawyers: PropTypes.arrayOf(PropTypes.object),
      Pages: PropTypes.arrayOf(PropTypes.object),
      Resources: PropTypes.arrayOf(PropTypes.object),
      EBarGreenbookResults: PropTypes.arrayOf(PropTypes.string),
      SearchLinks: PropTypes.shape({
        ClesSearchUrl: PropTypes.string,
        LawyerSearchUrl: PropTypes.string,
        PracticeLibrarySearchUrl: PropTypes.string,
        EventSearchUrl: PropTypes.string,
        EBarGreenbookSearchUrl: PropTypes.string,
      }),
    }),
    count: PropTypes.number,
    totalCount: PropTypes.number,
    loading: PropTypes.bool,
    error: PropTypes.string,
    filters: PropTypes.shape({
      keyword: PropTypes.string,
      section: PropTypes.array,
    }),
  }).isRequired,
  dispatch: PropTypes.func.isRequired,
  isMember: PropTypes.bool.isRequired,
};

const mapStateToProps = ({ siteSearch }) => ({
  siteSearch,
});

Results.displayName = 'Results';

export default connect(mapStateToProps)(Results);
